'use client';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../ui/heading';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function useScrollProgress(targetRef: React.RefObject<HTMLElement>) {
  const [progress, setProgress] = useState(0);

  useLayoutEffect(() => {
    const el = targetRef.current;
    if (!el) return;

    let raf = 0;

    const update = () => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 0;

      const p = (vh - rect.top) / Math.max(1, rect.height);

      const START = 0.25;
      const END = 1.35;

      const stretched = clamp((p - START) / (END - START), 0, 1);
      const eased = stretched * stretched * (3 - 2 * stretched);

      const FRICTION = 1.25;
      setProgress(Math.pow(eased, FRICTION));
    };

    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [targetRef]);

  return progress;
}

type LayoutInfo = {
  rankByIndex: number[];
  total: number;
};

function ProgressiveText({
  text,
  progress,
  className,
}: {
  text: string;
  progress: number;
  className?: string;
}) {
  const containerRef = useRef<HTMLParagraphElement | null>(null);
  const charRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const [layout, setLayout] = useState<LayoutInfo | null>(null);

  const chars = useMemo(() => Array.from(text), [text]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const measure = () => {
      const containerRect = container.getBoundingClientRect();

      const items: Array<{ i: number; top: number; left: number }> = [];
      for (let i = 0; i < chars.length; i++) {
        const node = charRefs.current[i];
        if (!node) continue;
        const r = node.getBoundingClientRect();
        if (r.width === 0 && r.height === 0) continue;

        items.push({
          i,
          top: r.top - containerRect.top,
          left: r.left - containerRect.left,
        });
      }

      if (!items.length) return;

      const LINE_EPS = 6;

      items.sort((a, b) => a.top - b.top || a.left - b.left);

      let currentLineTop: number | null = null;
      let lineTop = 0;

      for (const it of items) {
        if (currentLineTop === null || Math.abs(it.top - currentLineTop) > LINE_EPS) {
          currentLineTop = it.top;
          lineTop = currentLineTop;
        }
        it.top = lineTop;
      }

      items.sort((a, b) => a.top - b.top || a.left - b.left);

      const rankByIndex = new Array<number>(chars.length).fill(0);
      items.forEach((it, rank) => {
        rankByIndex[it.i] = rank;
      });

      setLayout({ rankByIndex, total: items.length });
    };

    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(container);

    document?.fonts?.ready?.then(measure).catch(() => undefined);

    return () => ro.disconnect();
  }, [chars.length]);

  const total = layout?.total ?? chars.length;
  const virtualPos = progress * total;

  return (
    <p
      ref={containerRef}
      className={cn(className, 'whitespace-pre-wrap break-words')}
      aria-label={text}
    >
      {chars.map((ch, i) => {
        const rank = layout?.rankByIndex?.[i] ?? i;
        const isRead = virtualPos > rank;

        return (
          <span
            key={i}
            ref={node => {
              charRefs.current[i] = node;
            }}
            className={cn('transition-colors duration-150', isRead ? 'text-primary' : 'text-black')}
          >
            {ch}
          </span>
        );
      })}
    </p>
  );
}

export function ReadingText({
  subtitle,
  content,
  className,
}: {
  subtitle: string;
  content: string;
  className?: string;
}) {
  const sectionRef = useRef<HTMLElement | null>(null);
  const readProgress = useScrollProgress(sectionRef);

  return (
    <section ref={sectionRef} className={cn('custom-section', className)}>
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-6 md:flex-row md:justify-between lg:gap-8',
        )}
      >
        <Heading subtitle={subtitle} />
        <div className="w-full sm:max-w-[1044px]">
          <ProgressiveText
            text={content}
            progress={readProgress}
            className={cn(
              'h2',
              'font-medium leading-[100%] -tracking-[6%] sm:leading-[100%] md:leading-[100%] lg:leading-[100%] xl:leading-[100%]',
            )}
          />
        </div>
      </div>
    </section>
  );
}
